import Vue from 'vue';
import values from 'lodash/values';
import isEmpty from 'lodash/isEmpty';
import { createFrozenMap } from '@/vuex/utils';
import {
    addItemToGroup,
    removeItemFromGroup,
    fetchGroups,
    fetchGroup,
    fetchGroupIsEmpty,
    deleteGroup,
    createGroup,
    updateGroup,
} from './groups.utils';

const SET_ERROR = 'setError';
const SET_FETCHING = 'setFetching';
const SET_MAP = 'setMap';
const SET_MAP_AT_KEY = 'setMapAtKey';
const DELETE_MAP_KEY = 'deleteMapKey';
const SET_SAVING = 'setSaving';
const SET_FILTER = 'setFilter';
const itemTypeToModuleName = {
    page: 'pages',
    feature: 'features',
    guide: 'guides',
    trackType: 'trackTypes',
    vocItem: 'vocItems',
    journey: 'journeys',
    omnichannel: 'emails',
};

let loadAllPromise;

export default {
    namespaced: true,
    state: {
        map: {},
        error: {},
        idFilter: [],
        fetching: false,
        saving: false,
    },
    mutations: {
        [SET_MAP] (state, { map }) {
            state.map = map;
        },
        [SET_ERROR] (state, { error }) {
            state.error = error;
        },
        [SET_FETCHING] (state, { fetching }) {
            state.fetching = fetching;
        },
        [SET_MAP_AT_KEY] (state, { id, group }) {
            Vue.set(state.map, id, group);
        },
        [DELETE_MAP_KEY] (state, { id }) {
            Vue.delete(state.map, id);
        },
        [SET_SAVING] (state, { saving }) {
            state.saving = saving;
        },
        [SET_FILTER] (state, { idFilter }) {
            state.idFilter = idFilter;
        },
    },
    actions: {
        async loadAll ({ commit, dispatch, rootState, getters }, { reFetch = false, items = false } = {}) {

            if (!reFetch) {
                const groups = values(rootState.groups.map);
                const groupsAreLoaded = !isEmpty(groups);
                const itemsAreLoaded = groupsAreLoaded && groups.every((group) => !isEmpty(group.items));

                const requestingItemsAndItemsAreLoaded = items && itemsAreLoaded;
                const requestingGroupsAndGroupsAreLoaded = !items && groupsAreLoaded;

                if (requestingItemsAndItemsAreLoaded || requestingGroupsAndGroupsAreLoaded) {
                    return;
                }
            }

            if (loadAllPromise) {
                await loadAllPromise;

                return;
            }

            commit(SET_FETCHING, { fetching: true });

            try {
                loadAllPromise = Promise.all([
                    fetchGroups({ items }),
                    dispatch(
                        'userSettings/loadOne',
                        {
                            namespaced: true,
                            name: 'groupFilter',
                        },
                        { root: true },
                    ),
                ]);

                const [{ data: groups }] = await loadAllPromise;

                const savedGroupFilter = getters.storedUserSettingGroupFilter;

                if (savedGroupFilter) {
                    commit(SET_FILTER, { idFilter: savedGroupFilter });
                }

                commit(SET_FETCHING, { fetching: false });
                commit(SET_MAP, {
                    map: createFrozenMap(groups),
                });
            } catch (error) {
                commit(SET_FETCHING, { fetching: false });
                commit('setError', { error });
                throw error;
            } finally {
                loadAllPromise = null;
            }
        },
        async loadOne ({ commit, getters }, { reFetch = false, ...payload }) {
            const { id } = payload;

            // Check if loaded in state unless force reFetch is true
            if (!reFetch && !!getters.groupById(id)) return;

            commit(SET_FETCHING, { fetching: true });

            try {
                const [{ data: group }, { data: isEmptyResult }] = await Promise.all([
                    fetchGroup(payload),
                    fetchGroupIsEmpty({ id }),
                ]);
                group.isEmpty = isEmptyResult.isempty;
                commit(SET_FETCHING, { fetching: false });
                commit(SET_MAP_AT_KEY, {
                    id: group.id,
                    group: Object.freeze(group),
                });
            } catch (error) {
                commit(SET_FETCHING, { fetching: false });
                commit(SET_ERROR, { error });
                throw error;
            }
        },
        async remove ({ commit }, { id }) {
            commit(SET_FETCHING, { fetching: true });

            try {
                await deleteGroup({ id });
                commit(SET_FETCHING, { fetching: false });
                commit(DELETE_MAP_KEY, { id });
            } catch (error) {
                commit(SET_FETCHING, { fetching: false });
                commit(SET_ERROR, { error });
                throw error;
            }
        },

        async create ({ commit }, payload) {
            try {
                commit(SET_SAVING, { saving: true });
                const result = await createGroup(payload);
                const group = result.data;
                commit(SET_MAP_AT_KEY, {
                    id: group.id,
                    group: Object.freeze(group),
                });

                return group;
            } catch (error) {
                commit(SET_ERROR, { error });
                throw error;
            } finally {
                commit(SET_SAVING, { saving: false });
            }
        },

        async update ({ commit }, payload) {
            try {
                commit(SET_SAVING, { saving: true });
                const result = await updateGroup(payload);
                const group = result.data;
                commit(SET_MAP_AT_KEY, {
                    id: group.id,
                    group: Object.freeze(group),
                });
            } catch (error) {
                commit(SET_ERROR, { error });
                throw error;
            } finally {
                commit(SET_SAVING, { saving: false });
            }
        },
        async addItemToGroup ({ commit, dispatch }, payload = {}) {
            commit(SET_SAVING, { saving: true });
            const { groupId, itemType, itemId, previousGroupId } = payload;
            if (!groupId) return;

            try {
                await addItemToGroup({ groupId, itemType, itemId });
                await dispatch(
                    `${itemTypeToModuleName[itemType]}/loadOne`,
                    { id: itemId, reFetch: true },
                    { root: true },
                ); // to update the page details page

                // refetch group so that items are up-to-date
                await dispatch('loadOne', { reFetch: true, items: true, id: groupId });

                // in some case we wish to make sure the previous group is also up-to-date
                if (previousGroupId) {
                    await dispatch('loadOne', { reFetch: true, items: true, id: previousGroupId });
                }
            } catch (savingError) {
                commit(SET_ERROR, { error: savingError });
                throw savingError;
            } finally {
                commit(SET_SAVING, { saving: false });
            }
        },
        async removeItemFromGroup ({ commit, dispatch }, payload = {}) {
            commit(SET_SAVING, { saving: true });
            const { groupId, itemType, itemId } = payload;
            if (!groupId) return;

            try {
                await removeItemFromGroup({ groupId, itemType, itemId });
                await dispatch(
                    `${itemTypeToModuleName[itemType]}/loadOne`,
                    { id: itemId, reFetch: true },
                    { root: true },
                ); // to update the page details page

                // refetch group so that items are up-to-date
                await dispatch('loadOne', { reFetch: true, items: true, id: groupId });
            } catch (savingError) {
                commit(SET_ERROR, { error: savingError });
                throw savingError;
            } finally {
                commit(SET_SAVING, { saving: false });
            }
        },
        updateFilter ({ commit, dispatch }, { idFilter }) {
            commit(SET_FILTER, { idFilter });

            dispatch(
                'userSettings/update',
                {
                    namespaced: true,
                    name: 'groupFilter',
                    value: idFilter,
                },
                { root: true },
            );
        },
        clearFilter ({ dispatch }) {
            dispatch('updateFilter', { idFilter: [] });
        },
    },
    getters: {
        list: (state) => values(state.map),
        storedUserSettingGroupFilter: (state, getters, rootState, rootGetters) => {
            const savedGroupFilter = rootGetters['userSettings/settingByName']('groupFilter', true);

            return savedGroupFilter.value || [];
        },
        groupById: (state) => (id) => state.map[id],
        groupByGuideId: (state, getters) => ({ guideId }) =>
            getters.list.find((group) => group.items.some((item) => item.itemId === guideId)),
        groupByEntityId: (state, getters) => (id) =>
            getters.list.find((group) => group.items.some((item) => item.itemId === id)),
    },
};
